<script lang="ts">
import ChatPanelHeader from '@/V2/chat/components/ChatPanelHeader.vue';
import ChatPanelPromptBox from '@/V2/chat/components/ChatPanelPromptBox.vue';
import ChatPanelMessages from '@/V2/chat/components/ChatPanelMessages.vue';
import ChatPanelEmptyState from '@/V2/chat/components/ChatPanelEmptyState.vue';
import { mapGetters, mapActions } from 'vuex';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
} from '@inscribe/signal';
import {
  ChatContext,
  ChatContextCustomer,
  ChatContextDocument,
  ChatContextType,
} from '@/types/chat';

export default {
  name: 'ChatPanel',
  components: {
    ChatPanelHeader,
    ChatPanelPromptBox,
    ChatPanelMessages,
    ChatPanelEmptyState,
    iButton,
  },
  data() {
    return {
      userHasScrolledUp: false,
      iButtonStyles,
      iButtonTypes,
      iButtonSizes,
      scrollTimeout: null,
    };
  },
  computed: {
    ...mapGetters([
      'chatIsOpen',
      'isChatAgentEnabled',
      'chatIsExpanded',
      'currentConversationUuid',
      'getCachedDocumentById',
      'getCachedCustomerById',
      'getMessagesForConversationUuid',
      'getIsGeneratingForConversationUuid',
      'getCancelTokenForConversationUuid',
    ]),
    currentCustomerId() {
      return this.$route.params.customer_id;
    },
    currentCustomer() {
      if (!this.currentCustomerId) {
        return undefined;
      }
      return this.getCachedCustomerById(this.currentCustomerId);
    },
    currentDocumentId() {
      return this.$route.params.document_id;
    },
    currentDocument() {
      if (!this.currentDocumentId) {
        return undefined;
      }
      return this.getCachedDocumentById(this.currentDocumentId);
    },
    contextCustomerUuids() {
      return this.currentCustomerId ? [this.currentCustomerId] : [];
    },
    contextDocumentIds() {
      return this.currentDocumentId ? [this.currentDocumentId] : [];
    },
    contexts() {
      const output: ChatContext[] = [];
      if (this.currentCustomer) {
        output.push({
          type: ChatContextType.Customer,
          customerUuid: this.currentCustomerId,
          customerName: this.currentCustomer.name,
        } as ChatContextCustomer);
      }
      if (this.currentDocument) {
        output.push({
          type: ChatContextType.Document,
          documentId: this.currentDocumentId,
          customerUuid: this.currentCustomerId,
          documentName: this.currentDocument.name,
        } as ChatContextDocument);
      }
      return output;
    },
    currentContextType() {
      if (this.currentDocument) {
        return ChatContextType.Document;
      } else if (this.currentCustomer) {
        return ChatContextType.Customer;
      } else {
        return ChatContextType.Global;
      }
    },
    messages() {
      return this.getMessagesForConversationUuid(this.currentConversationUuid);
    },
    isGenerating() {
      return this.getIsGeneratingForConversationUuid(
        this.currentConversationUuid,
      );
    },
    cancelToken() {
      return this.getCancelTokenForConversationUuid(
        this.currentConversationUuid,
      );
    },
  },
  mounted() {
    this.focusPromptBox();
  },
  watch: {
    chatIsOpen: {
      immediate: true,
      handler(newVal) {
        this.setChatIsOpen(newVal);
      },
    },
    messages: {
      immediate: true,
      deep: true,
      handler() {
        if (this.userHasScrolledUp) {
          return;
        }
        this.scrollToBottom();
      },
    },
  },
  methods: {
    ...mapActions(['submitConversationMessage', 'createNewConversationUuid']),
    createNewConversation() {
      this.createNewConversationUuid();
      this.userHasScrolledUp = false;
    },
    setChatIsOpen(isOpen: boolean) {
      if (!this.isChatAgentEnabled) {
        return;
      }
      const chatPanelElement = this.$refs.chatPanel;
      if (!chatPanelElement) {
        return;
      }

      // Focus the textarea on open
      if (isOpen) {
        this.focusPromptBox();
      }
      // During prototyping, hide the Intercom widget when chat panel is open
      const intercomWidget = document.querySelector<HTMLDivElement>(
        '.intercom-lightweight-app',
      );
      if (intercomWidget) {
        intercomWidget.style.display = isOpen ? 'none' : 'block';
      }
    },
    stopGeneration() {
      if (this.cancelToken) {
        this.cancelToken.abort();
      }
    },
    async onMessageSubmitted(userMessageContent: string) {
      this.submitConversationMessage({
        conversationUuid: this.currentConversationUuid,
        userMessageContent,
        contextCustomerUuids: this.contextCustomerUuids,
        contextDocumentIds: this.contextDocumentIds,
      });
    },
    handleMessageAreaScroll(event) {
      const container = event?.target;
      // Calculate the distance from the current scroll position to the bottom of the container
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      const nearBottom = distanceFromBottom <= 20;
      // Toggle the flag based on whether the user is near the bottom
      this.userHasScrolledUp = !nearBottom;
    },
    scrollToBottom() {
      const scrollArea = document.querySelector(
        '.chat-panel-messages-container',
      );
      if (!scrollArea) {
        return;
      }
      this.$nextTick(() => {
        scrollArea.scrollTop = scrollArea.scrollHeight;
      });
      // Reset user scroll state now that we're at the bottom
      this.userHasScrolledUp = false;
    },
    focusPromptBox() {
      this.$nextTick(() => {
        const promptBoxInput = document.querySelector<HTMLTextAreaElement>(
          '[data-testid="chat-panel-prompt-box-textarea"]',
        );
        promptBoxInput?.focus();
      });
    },
  },
};
</script>

<template>
  <div
    :class="{ wide: chatIsExpanded, 'is-open': chatIsOpen }"
    class="chat-panel d-flex flex-column justify-content-space-between shadow-3xl"
    ref="chatPanel"
  >
    <!-- chat header -->
    <chat-panel-header class="i-m-9" @clear-chat="createNewConversation" />

    <!-- empty state -->
    <chat-panel-empty-state
      v-if="messages.length === 0"
      class="i-m-9"
      :current-context-type="currentContextType"
      :contexts="contexts"
      :document="currentDocument"
    />

    <!-- messages -->
    <div
      v-else
      class="chat-panel-messages-container"
      @scroll="handleMessageAreaScroll"
    >
      <chat-panel-messages
        class="chat-panel-messages-container i-m-9"
        :conversation-uuid="currentConversationUuid"
        :user-has-scrolled-up="userHasScrolledUp"
      />
    </div>

    <!-- prompt box -->
    <chat-panel-prompt-box
      class="i-mt-9 i-mx-9"
      :contexts="contexts"
      :conversation-uuid="currentConversationUuid"
      @new-chat="createNewConversation"
      @stop-generation="stopGeneration"
      @submit-message="onMessageSubmitted"
    />

    <!-- disclaimer -->
    <div
      class="d-flex flex-row justify-content-center i-text-body-3 i-text-neutral-90 i-p-3"
    >
      The Assistant can make mistakes.
    </div>

    <!-- Scroll to bottom button -->
    <i-button
      :button-style="iButtonStyles.SOLID_LIGHT"
      :size="iButtonSizes.SMALL"
      :type="iButtonTypes.NEUTRAL"
      left-icon="medium-arrows-down"
      :class="{ visible: userHasScrolledUp }"
      class="scroll-to-bottom-button"
      @button-click="scrollToBottom"
    ></i-button>
  </div>
</template>

<style scoped>
.chat-panel {
  display: flex;
  position: fixed;
  top: 12px;
  right: -100%;
  bottom: 12px;
  width: 460px;
  min-width: 460px;
  background-color: var(--base);
  z-index: 10000;
  border: 1px solid var(--border-neutral-subtle);
  border-radius: 32px;
  transition:
    right 100ms ease-in-out,
    width 150ms ease-in-out,
    opacity 150ms ease-in-out;
  opacity: 0;

  &.wide {
    width: 50%;
  }

  &.is-open {
    right: 12px;
    opacity: 1;
  }

  .chat-panel-messages-container {
    position: relative;
    overflow-y: auto;
    flex-grow: 1;
  }

  .scroll-to-bottom-button {
    position: absolute;
    bottom: 144px;
    right: 20px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 150ms ease-in-out;

    &.visible {
      opacity: 1;
      pointer-events: auto;
    }
  }
}
</style>
